import { dayKey, weekdayOf } from "./timeRange.js";
import { rollingWindowStart } from "./weekWindow.js";

// #508: the athlete's usual training days, deduced from when they actually
// trained, correctable in the profile (`Profile.habitualWeekdays`). The
// correction wins over the deduction; too little history reads as "unknown",
// never as a guess. Same weekday convention as `weekStartDay`: 0 = Sunday ..
// 6 = Saturday.
//
// #510: the day is read in the athlete's chosen `Profile.timeZone`, through
// the same dayKey as sessionMuscleAdjacency.ts. Without one it is the local
// clock, which on the API is the Lambda's (UTC).

/** How many recent weeks the deduction looks at. */
export const HABITUAL_WINDOW_WEEKS = 6;

/** Share of the window's weeks a weekday needs to count as usual. */
export const HABITUAL_MIN_SHARE = 0.5;

export type HabitualWeekdaysSource = "inferred" | "manual" | "unknown";

export type HabitualWeekdays = {
  /** Usual weekdays in week order from `weekStartDay`; null when unknown. */
  weekdays: number[] | null;
  source: HabitualWeekdaysSource;
  /** The profile or the log could not be read: unknown is not "too little
   *  history". */
  readFailed?: boolean;
};

const isWeekday = (d: number): boolean => Number.isInteger(d) && d >= 0 && d <= 6;

/**
 * The weekdays the athlete trained on in at least half of the last
 * `windowWeeks` weeks, ascending. null when no weekday reaches it: the
 * history is too short or too irregular to tell.
 */
export const inferHabitualWeekdays = (
  startedAtMs: ReadonlyArray<number>,
  nowMs: number,
  windowWeeks: number = HABITUAL_WINDOW_WEEKS,
  timeZone?: string,
): number[] | null => {
  const from = rollingWindowStart(nowMs, windowWeeks * 7);
  const datesByWeekday = new Map<number, Set<string>>();
  for (const ms of startedAtMs) {
    if (!Number.isFinite(ms) || ms < from || ms > nowMs) continue;
    const weekday = weekdayOf(ms, timeZone);
    const set = datesByWeekday.get(weekday) ?? new Set<string>();
    set.add(dayKey(ms, timeZone));
    datesByWeekday.set(weekday, set);
  }
  const min = Math.ceil(windowWeeks * HABITUAL_MIN_SHARE);
  const out = [...datesByWeekday.entries()]
    .filter(([, dates]) => dates.size >= min)
    .map(([weekday]) => weekday)
    .sort((a, b) => a - b);
  return out.length > 0 ? out : null;
};

/** `weekdays` de-duplicated, in week order starting at `weekStartDay`. */
export const weekdaysInWeekOrder = (
  weekdays: ReadonlyArray<number>,
  weekStartDay: number,
): number[] =>
  [...new Set(weekdays.filter(isWeekday))].sort(
    (a, b) => ((a - weekStartDay + 7) % 7) - ((b - weekStartDay + 7) % 7),
  );

/** The athlete's correction when there is one, else the deduction, else
 *  unknown. */
export const resolveHabitualWeekdays = (
  manual: ReadonlyArray<number> | null | undefined,
  inferred: ReadonlyArray<number> | null,
  weekStartDay: number,
): HabitualWeekdays => {
  const own = manual ? weekdaysInWeekOrder(manual, weekStartDay) : [];
  if (own.length > 0) return { weekdays: own, source: "manual" };
  const deduced = inferred ? weekdaysInWeekOrder(inferred, weekStartDay) : [];
  if (deduced.length > 0) return { weekdays: deduced, source: "inferred" };
  return { weekdays: null, source: "unknown" };
};

export const HABITUAL_WEEKDAYS_UNKNOWN: HabitualWeekdays = {
  weekdays: null,
  source: "unknown",
};
